import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { WorkRule, WorkRulesBase } from 'repository/timekeeper/@types/WorkRuleModel';
import { BreakRule } from 'repository/timekeeper/@types/BreakRuleModel';
import { BonusRule } from 'repository/timekeeper/@types/BonusRuleModel';
import { ScheduleDesviationRule } from 'repository/timekeeper/@types/ScheduleDesviationRuleModel';
import { OvertimeRule } from 'repository/timekeeper/@types/OvertimeRuleModel';
import { Zone } from 'repository/timekeeper/@types/ZoneModel';
import { useWorkRuleActions } from 'hooks/workRules/useWorkRulesActions';
import { normalizeString } from 'utils/Validations';

interface Props {
    rows: WorkRule[];
    onClose: () => void;
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info' | 'duplicate';
    selectedRow: WorkRule[] | undefined;
    open: boolean;
}

const EMPTY_FORM: WorkRule = {
    work_rule_id: 0,
    id: 0,
    name: '',
    effective_date: '',
    description: '',
    excepcion_rule_id: 0,
    paycodes_distribution_id: 0,
    rounding_rule_id: 0,
    bonus: [] as BonusRule[],
    break_rules: [] as BreakRule[],
    overtime_rules: [] as OvertimeRule[],
    zones: [] as Zone[],
    schedule_desviation_rules: [] as ScheduleDesviationRule[],
    automatic_break: null,
};

const toFormFromWorkRule = (workRule: WorkRule): WorkRule => ({
    work_rule_id: workRule.work_rule_id,
    id: workRule.id,
    name: workRule.name,
    effective_date: workRule.effective_date,
    description: workRule.description,
    excepcion_rule_id: workRule.excepcion_rule_id,
    paycodes_distribution_id: workRule.paycodes_distribution_id,
    rounding_rule_id: workRule.rounding_rule_id,
    bonus: workRule.bonus?.map((b: any) => ({ id: b.bonus_rule_id ?? b.id, name: b.name })) as any,
    break_rules: workRule.break_rules?.map((b: any) => ({ id: b.break_rule_id ?? b.id, name: b.name })) as any,
    overtime_rules: workRule.overtime_rules?.map((o: any) => ({ id: o.overtime_rule_id ?? o.id, name: o.name })) as any,
    zones: workRule.zones?.map((z: any) => ({ id: z.zone_id ?? z.id, name: z.name })) as any,
    schedule_desviation_rules: workRule.schedule_desviation_rules?.map((s: any) => ({ id: s.schedule_desviation_rule_id ?? s.id, name: s.name })) as any,
    automatic_break: workRule.automatic_break,
});

export const useWorkRulesForm = ({ rows, onClose, reloading, mode, selectedRow, open }: Props) => {
    const currentWorkRule = mode === 'edit' || mode === 'info' ? selectedRow?.[0] : undefined;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { addWorkRule, updateWorkRule, deleteWorkRules } = useWorkRuleActions();
    const [initialForm, setInitialForm] = useState<WorkRule>(EMPTY_FORM);
    const [form, setForm] = useState<WorkRule>(EMPTY_FORM);
    const [alert, setAlert] = useState(false);
    const [value, setValue] = useState('1');

    useEffect(() => {
        if (!open) return;

        if ((mode === 'edit' || mode === 'info') && currentWorkRule) {
            const preloadedForm = toFormFromWorkRule(currentWorkRule);
            setForm(preloadedForm);
            setInitialForm(preloadedForm);
            return;
        }
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
    }, [open, mode, currentWorkRule]);

    const update = <K extends keyof WorkRulesBase>(field: K, value: WorkRulesBase[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        if (mode === 'add') {
            addWorkRule({
                setLoading: () => {},
                body: form,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.work_rules.alerts.add.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.work_rules.alerts.add.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
            return;
        }

        if (mode === 'edit' && currentWorkRule) {
            updateWorkRule({
                setLoading: () => {},
                workRuleId: currentWorkRule.id,
                body: form,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.work_rules.alerts.edit.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.work_rules.alerts.edit.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
            return;
        }

        if (mode === 'delete' && currentWorkRule) {
            deleteWorkRules({
                setLoading: () => {},
                workRuleIds: [currentWorkRule.id],
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.work_rules.dialog.alerts.delete.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Éxito',
                    message: t('settings.work_rules.dialog.alerts.delete.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }
    };

    const existName = useMemo(() => {
        return (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row: any) => {
                if (mode === 'edit' && currentWorkRule && row.id === currentWorkRule.id) return false;
                return normalizeString(row.name) === normalizedInput;
            });
        };
    }, [rows, mode, currentWorkRule]);

    const disabled = useMemo(() => {
        return (mode === 'edit' && JSON.stringify(form) === JSON.stringify(initialForm)) || (mode === 'add' && !form.name);
    }, [mode, form, initialForm]);

    const cleanForm = () => {
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
        setAlert(false);
        onClose();
    };

    const hasChanges = useMemo(() => {
        return JSON.stringify(form) !== JSON.stringify(initialForm);
    }, [form, initialForm]);

    const detectChanges = () => {
        if (hasChanges) {
            setAlert(true);
        } else {
            cleanForm();
        }
    };

    return {
        form,
        alert,
        value,
        update,
        disabled,
        setValue,
        setAlert,
        existName,
        cleanForm,
        hasChanges,
        handleSubmit,
        detectChanges,
        currentWorkRule,
    };
};
